import { jsx as _jsx } from "react/jsx-runtime";
import { invoke } from '@tauri-apps/api/core';
import { DesktopLogin } from 'components/organisms/desktop-login';
import { DesktopUpdater } from 'components/organisms/desktop-updater';
import { QuickEntryOverlay } from 'components/organisms/nitro-claw/quick-entry/quick-entry-overlay';
import { DESKTOP_AUTH_HANDOFF_EVENT, useDesktopDeepLinkHandler } from 'hooks/use-desktop-deep-link-handler';
import { useTranslate } from 'hooks/use-translation';
import { useEffect, useState } from 'react';
import { hasDesktopSession } from 'services/desktop-session';
import { SetupOkta } from 'setupokta';
import { useDismissBootSkeleton } from 'utils/dismiss-boot-skeleton';
import { isQuickEntryWindow } from 'utils/desktop-window';
import { isTauriApp } from 'utils/is-tauri';
/**
 * In Tauri, unauthenticated users must not hit tenant get-meta / SetupOkta /
 * Okta OAuth redirects (those cause a full navigation flicker loop).
 * Gate on the RocketlaneDesktop userAgent — available on first paint.
 *
 * The Quick Entry overlay window skips the full app boot and renders a lightweight
 * composer that talks to the main window over Tauri events.
 */ export const DesktopBootGate = ()=>{
    const translate = useTranslate();
    const inTauri = isTauriApp();
    const inQuickEntry = inTauri && isQuickEntryWindow();
    const [hasSession, setHasSession] = useState(()=>hasDesktopSession());
    const showDesktopLogin = inTauri && !hasSession && !inQuickEntry;
    // Dismiss for both login and authenticated paths — otherwise a remount that
    // briefly sits in SetupOkta PageLoading leaves the HTML boot overlay up.
    useDismissBootSkeleton(inTauri);
    useDesktopDeepLinkHandler();
    // Push localized tray labels to Rust once after IntlProvider resolves.
    useEffect(()=>{
        if (!inTauri) {
            return;
        }
        void invoke('update_tray_labels', {
            show: translate('desktop.tray.open'),
            ask: translate('desktop.tray.ask'),
            quit: translate('desktop.tray.quit'),
            tooltip: translate('desktop.tray.tooltip')
        }).catch(()=>{
        // Non-fatal: tray may not be available on all platforms.
        });
    }, [
        inTauri,
        translate
    ]);
    useEffect(()=>{
        const onHandoff = (event)=>{
            const detail = event.detail;
            if ((detail === null || detail === void 0 ? void 0 : detail.status) === 'success' && hasDesktopSession()) {
                setHasSession(true);
            }
        };
        window.addEventListener(DESKTOP_AUTH_HANDOFF_EVENT, onHandoff);
        return ()=>{
            window.removeEventListener(DESKTOP_AUTH_HANDOFF_EVENT, onHandoff);
        };
    }, []);
    if (inQuickEntry) {
        return /*#__PURE__*/ _jsx(QuickEntryOverlay, {});
    }
    return /*#__PURE__*/ _jsx(DesktopUpdater, {
        children: showDesktopLogin ? /*#__PURE__*/ _jsx(DesktopLogin, {}) : /*#__PURE__*/ _jsx(SetupOkta, {})
    });
};
